import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Check, CalendarCheck, Footprints, Dumbbell, Camera } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip, Cell } from "recharts";

const RUN_TYPES = new Set(["easy", "long", "recovery", "tempo", "marathon", "speed", "interval", "intervals", "threshold", "race", "run", "strength_run", "walk", "hike", "trail"]);
const FEEL = { very_weak: "Very weak", weak: "Weak", normal: "Normal", strong: "Strong", very_strong: "Very strong" };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const localDay = (s) => new Date(`${s}T00:00:00`);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const fmtMin = (m) => { const v = Math.round(m || 0); if (!v) return ""; const h = Math.floor(v / 60); return h ? `${h}h ${String(v % 60).padStart(2, "0")}m` : `${v} min`; };

/* ------------------------------------------------------------------ calendar */
export function ActiveDaysCalendar({ activeDays, today }) {
  const [month, setMonth] = useState(() => { const t = localDay(today); return new Date(t.getFullYear(), t.getMonth(), 1); });
  const set = useMemo(() => new Set(activeDays), [activeDays]);
  const first = month;
  const gridStart = mondayOf(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const rows = Array.from({ length: 6 }, (_, r) => cells.slice(r * 7, r * 7 + 7));
  const inMonth = (d) => d.getMonth() === month.getMonth();
  const monthActive = activeDays.filter((d) => d.startsWith(iso(first).slice(0, 7))).length;
  const prevMonthKey = iso(new Date(month.getFullYear(), month.getMonth() - 1, 1)).slice(0, 7);
  const prevActive = activeDays.filter((d) => d.startsWith(prevMonthKey)).length;

  // Consecutive weeks with ≥1 completed workout (no day streak — rest days
  // are prescribed, so a gap between workouts isn't a broken streak).
  let weekStreak = 0;
  let wk = mondayOf(localDay(today));
  for (;;) {
    const hit = Array.from({ length: 7 }, (_, i) => iso(addDays(wk, i))).some((d) => set.has(d));
    if (!hit) break;
    weekStreak += 1; wk = addDays(wk, -7);
    if (weekStreak > 520) break;
  }
  const canGoNext = month < new Date(localDay(today).getFullYear(), localDay(today).getMonth(), 1);
  const diff = monthActive - prevActive;

  return (
    <section className="bg-white border border-[var(--tir-border)] p-5" data-testid="progress-calendar">
      <h2 className="font-serif-display text-2xl text-[var(--tir-ink)] mb-4">Active days</h2>
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="progress-cal-prev" className="p-2 text-[var(--tir-muted)] hover:text-[var(--tir-ink)]" aria-label="Previous month"><ChevronLeft size={18} /></button>
        <div className="text-sm font-semibold tracking-wide" data-testid="progress-cal-month">{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</div>
        <button type="button" disabled={!canGoNext} onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="progress-cal-next" className="p-2 text-[var(--tir-muted)] hover:text-[var(--tir-ink)] disabled:opacity-30" aria-label="Next month"><ChevronRight size={18} /></button>
      </div>
      <div className="grid grid-cols-[repeat(7,1fr)_28px] gap-y-1 text-center">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <div key={d} className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] py-1">{d}</div>
        ))}
        <div />
        {rows.map((row, r) => {
          const weekHit = row.some((d) => set.has(iso(d)));
          const weekPast = row[0] <= localDay(today);
          return (
            <React.Fragment key={r}>
              {row.map((d, i) => {
                const k = iso(d);
                const on = set.has(k);
                const prevOn = i > 0 && set.has(iso(row[i - 1]));
                const nextOn = i < 6 && set.has(iso(row[i + 1]));
                const isToday = k === today;
                const future = d > localDay(today);
                return (
                  <div key={k} className="relative h-10 flex items-center justify-center">
                    {on && (prevOn || nextOn) && (
                      <span className={`absolute inset-y-1.5 bg-[var(--tir-navy)]/10 ${prevOn ? "left-0" : "left-1/2 rounded-l-full"} ${nextOn ? "right-0" : "right-1/2 rounded-r-full"}`} />
                    )}
                    <span
                      data-testid={on ? `progress-day-active-${k}` : undefined}
                      className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-medium transition-colors
                        ${on ? "bg-[var(--tir-navy)] text-white shadow-[0_2px_6px_rgba(26,32,44,0.25)]" : future ? "text-[var(--tir-border)]" : inMonth(d) ? "text-[var(--tir-ink)]" : "text-[var(--tir-muted)]/50"}
                        ${isToday && !on ? "ring-2 ring-[var(--tir-burgundy)]" : ""} ${isToday && on ? "ring-2 ring-[var(--tir-burgundy)] ring-offset-1" : ""}`}
                    >
                      {d.getDate()}
                    </span>
                  </div>
                );
              })}
              <div className="h-10 flex items-center justify-center">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center ${weekHit ? "bg-[var(--tir-burgundy)] text-white" : weekPast ? "bg-[var(--tir-border)] text-white" : "bg-transparent"}`} data-testid={weekHit ? `progress-week-hit-${r}` : undefined}>
                  {(weekHit || weekPast) && <Check size={12} strokeWidth={3} />}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-5">
        {[[monthActive, "Active days", CalendarCheck], [weekStreak, "Week streak", Check]].map(([v, l, Icon]) => (
          <div key={l} data-testid={`progress-stat-${l.toLowerCase().replace(" ", "-")}`}>
            <div className="font-serif-display text-4xl text-[var(--tir-burgundy)] leading-none">{v}</div>
            <div className="text-[11px] tracking-[0.14em] uppercase text-[var(--tir-muted)] mt-1 inline-flex items-center gap-1"><Icon size={11} />{l}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 bg-[var(--tir-ivory)] border border-[var(--tir-border)] px-3 py-2 text-xs text-[var(--tir-ink)]" data-testid="progress-month-compare">
        {diff === 0 ? "Same number of active days as last month" : `${Math.abs(diff)} ${diff > 0 ? "more" : "fewer"} active day${Math.abs(diff) === 1 ? "" : "s"} vs. previous month`}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ chart */
const RANGES = [["1M", 5], ["3M", 13], ["6M", 26], ["1Y", 52]];
export function DistanceChart({ weeks }) {
  const [range, setRange] = useState(13);
  const [metric, setMetric] = useState("km");
  const data = weeks.slice(-range);
  const total = data.reduce((s, w) => s + (metric === "km" ? w.km : w.minutes), 0);
  const prev = weeks.slice(-range * 2, -range).reduce((s, w) => s + (metric === "km" ? w.km : w.minutes), 0);
  const avg = data.length ? total / data.length : 0;
  const sessions = data.reduce((s, w) => s + w.sessions, 0);
  const perSession = sessions ? total / sessions : 0;
  const fmt = (v) => (metric === "km" ? `${v.toFixed(v >= 100 ? 0 : 1)} km` : fmtMin(v) || "0 min");
  const first = data[0]?.week_start, last = data[data.length - 1]?.week_start;
  const lbl = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" });
  const delta = total - prev;
  return (
    <section className="bg-white border border-[var(--tir-border)] p-5" data-testid="progress-chart">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-serif-display text-2xl text-[var(--tir-ink)]">{metric === "km" ? "Total distance" : "Total time"}</h2>
        <div className="flex border border-[var(--tir-border)] text-[10px] tracking-[0.16em] uppercase font-semibold">
          {["km", "time"].map((m) => (
            <button key={m} type="button" onClick={() => setMetric(m)} data-testid={`progress-metric-${m}`} className={`px-3 py-1.5 ${metric === m ? "bg-[var(--tir-navy)] text-white" : "text-[var(--tir-muted)]"}`}>{m === "km" ? "Distance" : "Time"}</button>
          ))}
        </div>
      </div>
      <div className="text-[11px] text-[var(--tir-muted)] mb-3">{first && last ? `${lbl(first)} – ${lbl(iso(addDays(localDay(last), 6)))}` : ""}</div>
      <div className="font-serif-display text-5xl text-[var(--tir-ink)] leading-none" data-testid="progress-total">{fmt(total)}</div>
      <div className="text-xs text-[var(--tir-muted)] mt-1">
        Total · <span className={delta >= 0 ? "text-emerald-700" : "text-[var(--tir-burgundy)]"} data-testid="progress-delta">{delta >= 0 ? "▲" : "▼"} {fmt(Math.abs(delta))}</span> vs previous period
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="bg-[var(--tir-ivory)] border border-[var(--tir-border)] p-3"><div className="font-semibold text-lg">{fmt(avg)}</div><div className="text-[10px] tracking-[0.14em] uppercase text-[var(--tir-muted)]">Weekly avg</div></div>
        <div className="bg-[var(--tir-ivory)] border border-[var(--tir-border)] p-3"><div className="font-semibold text-lg">{fmt(perSession)}</div><div className="text-[10px] tracking-[0.14em] uppercase text-[var(--tir-muted)]">Per session</div></div>
      </div>
      <div className="h-52 mt-4 -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="week_start" tickFormatter={(d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short" })} interval={Math.max(0, Math.floor(data.length / 4) - 1)} tick={{ fontSize: 10, fill: "#6f6a66" }} axisLine={false} tickLine={false} />
            <YAxis orientation="right" width={34} tick={{ fontSize: 10, fill: "#6f6a66" }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "rgba(26,32,44,0.05)" }} formatter={(v) => [fmt(Number(v)), metric === "km" ? "Distance" : "Time"]} labelFormatter={(d) => `Week of ${lbl(d)}`} contentStyle={{ fontSize: 12, border: "1px solid #e5e0d8" }} />
            <ReferenceLine y={avg} stroke="#7a1f2b" strokeDasharray="4 4" />
            <Bar dataKey={metric === "km" ? "km" : "minutes"} radius={[4, 4, 0, 0]}>
              {data.map((w, i) => <Cell key={w.week_start} fill={i === data.length - 1 ? "#7a1f2b" : "#1a202c"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex bg-[var(--tir-ivory)] border border-[var(--tir-border)] mt-3 text-[11px] tracking-[0.16em] uppercase font-semibold">
        {RANGES.map(([l, n]) => (
          <button key={l} type="button" onClick={() => setRange(n)} data-testid={`progress-range-${l}`} className={`flex-1 py-2 ${range === n ? "bg-[var(--tir-navy)] text-white" : "text-[var(--tir-muted)]"}`}>{l}</button>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ list */
export function ActivityList({ activities }) {
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(20);
  const shown = activities.filter((a) => filter === "all" || (filter === "run" ? RUN_TYPES.has(a.type) : !RUN_TYPES.has(a.type)));
  return (
    <section className="bg-white border border-[var(--tir-border)] p-5" data-testid="progress-activities">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif-display text-2xl text-[var(--tir-ink)]"><span data-testid="progress-activity-count">{shown.length}</span> activities</h2>
        <div className="flex border border-[var(--tir-border)] text-[10px] tracking-[0.16em] uppercase font-semibold">
          {[["all", "All"], ["run", "Runs"], ["other", "Other"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => { setFilter(k); setLimit(20); }} data-testid={`progress-filter-${k}`} className={`px-3 py-1.5 ${filter === k ? "bg-[var(--tir-burgundy)] text-white" : "text-[var(--tir-muted)]"}`}>{l}</button>
          ))}
        </div>
      </div>
      {shown.length === 0 && <p className="text-sm text-[var(--tir-muted)] italic">Nothing logged yet — completed workouts will show up here.</p>}
      <ul className="space-y-2">
        {shown.slice(0, limit).map((a) => {
          const run = RUN_TYPES.has(a.type);
          const Icon = run ? Footprints : Dumbbell;
          const bits = [a.distance_km ? `${a.distance_km} km` : null, fmtMin(a.duration_min) || null].filter(Boolean);
          const typeLabel = (a.type || "").replace("_", " ");
          const title = a.title && a.title !== "Workout" ? a.title : typeLabel ? typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1) + (run ? " run" : "") : "Workout";
          return (
            <li key={a.id} className="flex items-center gap-3 border border-[var(--tir-border)] bg-[var(--tir-ivory)]/50 px-3 py-2.5" data-testid={`progress-activity-${a.id}`}>
              <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${run ? "bg-[var(--tir-navy)] text-white" : "bg-[var(--tir-burgundy)] text-white"}`}><Icon size={16} /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[var(--tir-ink)] truncate">{bits.length ? `${bits.join(" · ")} · ` : ""}{title}</div>
                <div className="text-[10px] tracking-[0.14em] uppercase text-[var(--tir-muted)]">
                  {(a.type || "workout").replace("_", " ")}{a.source === "athlete" ? " · added by you" : ""}{a.status === "partial" ? " · partial" : ""}
                </div>
                <div className="text-[11px] text-[var(--tir-muted)] mt-0.5">{new Date(a.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "numeric", day: "numeric", year: "2-digit" })}{a.feeling ? ` · felt ${FEEL[a.feeling] || a.feeling}` : ""}</div>
              </div>
              {a.photo_count > 0 && <span className="text-[var(--tir-muted)] inline-flex items-center gap-0.5 text-[11px]"><Camera size={12} />{a.photo_count}</span>}
              {a.rpe != null && <span className="font-mono-tir text-sm font-semibold text-[var(--tir-ink)]">{a.rpe}<span className="text-[9px] text-[var(--tir-muted)]"> RPE</span></span>}
            </li>
          );
        })}
      </ul>
      {shown.length > limit && (
        <button type="button" onClick={() => setLimit((l) => l + 20)} data-testid="progress-activities-more" className="mt-3 w-full py-2 text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-navy)] border border-[var(--tir-border)] hover:border-[var(--tir-navy)]">Show more</button>
      )}
    </section>
  );
}


/** Calendar + chart + list in one go (used by athlete Progress tab and coach athlete page). */
export function ProgressWidgets({ data, today }) {
  return (
    <>
      <ActiveDaysCalendar activeDays={data.active_days} today={today} />
      <DistanceChart weeks={data.weeks} />
      <ActivityList activities={data.activities} />
    </>
  );
}
